/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Sync external context", description = "Sync governed metric and dimension definitions from Snowflake semantic views or Databricks metric views, or files from a GitHub repository, into Context Hub so agents use your team's definitions.", lastUpdatedDate = new Date(1792368000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Sync external context",
        description: "Sync governed metric and dimension definitions from Snowflake semantic views or Databricks metric views, or files from a GitHub repository, into Context Hub so agents use your team's definitions.",
        lastUpdatedDate: "Mon Oct 19 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Grant warehouse access",
          "depth": 0,
          "id": "grant-warehouse-access"
        }, {
          "value": "Snowflake grants",
          "depth": 1,
          "id": "snowflake-grants"
        }, {
          "value": "Databricks grants",
          "depth": 1,
          "id": "databricks-grants"
        }, {
          "value": "Sync semantic or metric views",
          "depth": 0,
          "id": "sync-semantic-or-metric-views"
        }, {
          "value": "How agents use synced definitions",
          "depth": 0,
          "id": "how-agents-use-synced-definitions"
        }, {
          "value": "Synced definitions update every hour",
          "depth": 0,
          "id": "synced-definitions-update-every-hour"
        }, {
          "value": "Sync files from GitHub",
          "depth": 0,
          "id": "sync-files-from-github"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      h3: "h3",
      code: "code",
      pre: "pre",
      span: "span",
      ol: "ol",
      li: "li",
      img: "img",
      ul: "ul"
    }, _provideComponents(), props.components), {FeatureFlag} = _components;
    if (!FeatureFlag) _missingMdxReference("FeatureFlag", true);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Data teams, platform admins"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: ["The ", _jsxs(_components.a, {
                    href: "/workspace-management/roles#agents",
                    children: [_jsx(_components.strong, {
                      children: "Configure Agents"
                    }), " grant"]
                  }), "."]
                }), _jsxs("li", {
                  children: [_jsx(_components.a, {
                    href: "/agents/overview",
                    children: "Agents"
                  }), ", ", _jsx(_components.a, {
                    href: "/ad-studio/overview",
                    children: "Ad Studio"
                  }), ", or ", _jsx(_components.a, {
                    href: "/lifecycle-studio/overview",
                    children: "Lifecycle Studio"
                  }), " enabled in your workspace."]
                }), _jsxs("li", {
                  children: ["For warehouse views, a ", _jsx(_components.a, {
                    href: "/sources/overview#add-a-source",
                    children: "Snowflake or Databricks source"
                  }), " whose credentials can read the semantic or metric views you want to sync."]
                }), _jsxs("li", {
                  children: ["For GitHub, ", _jsx(_components.a, {
                    href: "/extensions/git-sync#authenticate-to-git",
                    children: "Git Sync credentials"
                  }), " for your repository."]
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Sync metric and dimension definitions from Snowflake or Databricks, or files from GitHub, into Context Hub so agents can use the definitions maintained by your team."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "If your team defines metrics and dimensions in Snowflake or Databricks, you can sync those definitions into Context Hub instead of maintaining separate copies."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Warehouse"
            }), _jsx(_components.th, {
              children: "Supported views"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Snowflake"
            }), _jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Snowflake Semantic Views"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Databricks"
            }), _jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Databricks Unity Catalog Metric Views"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Each selected view appears as a file in ", _jsx(_components.strong, {
          children: "Context Hub > Knowledge > Files"
        }), ", marked ", _jsx(_components.strong, {
          children: "Synced"
        }), ". Hightouch reads the definitions from your warehouse without modifying the original views."]
      }), "\n", _jsxs(_components.p, {
        children: ["If your team keeps definitions, glossaries, or playbooks in a GitHub repository, you can ", _jsx(_components.a, {
          href: "#sync-files-from-github",
          children: "sync those files"
        }), " too."]
      }), "\n", _jsxs(_components.p, {
        children: ["Use synced views for definitions maintained in your warehouse. Use ", _jsx(_components.a, {
          href: "/context-hub/business-context#add-business-context",
          children: "Business context"
        }), " for additional business rules, instructions, and knowledge."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "grant-warehouse-access",
        children: "Grant warehouse access"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch reads views with the credentials of the source you connect. Before you sync, grant the source's Snowflake role or Databricks service principal access to the views you want agents to use. Hightouch only lists views those credentials can see."
      }), "\n", _jsx(_components.h3, {
        id: "snowflake-grants",
        children: "Snowflake grants"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lists semantic views across your Snowflake account, so views in any database the source's role can access appear, not only views in the source's default database."
      }), "\n", _jsxs(_components.p, {
        children: ["Replace ", _jsx(_components.code, {
          children: "YOUR_DATABASE"
        }), ", ", _jsx(_components.code, {
          children: "YOUR_SCHEMA"
        }), ", and ", _jsx(_components.code, {
          children: "HIGHTOUCH_ROLE"
        }), " with your database, schema, and the role configured for your Snowflake source:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-comment",
            children: "-- Allow the role to have basic access to the database and schema"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " USAGE ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " DATABASE YOUR_DATABASE ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE HIGHTOUCH_ROLE;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " USAGE ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " SCHEMA YOUR_DATABASE.YOUR_SCHEMA ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE HIGHTOUCH_ROLE;\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "-- Allow the role to have read access to all current and future semantic views in this schema"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), ", ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "REFERENCES"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ALL"
          }), " SEMANTIC VIEWS ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "IN"
          }), " SCHEMA YOUR_DATABASE.YOUR_SCHEMA ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE HIGHTOUCH_ROLE;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), ", ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "REFERENCES"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " FUTURE SEMANTIC VIEWS ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "IN"
          }), " SCHEMA YOUR_DATABASE.YOUR_SCHEMA ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE HIGHTOUCH_ROLE;\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Run the schema grants for each schema that holds semantic views you want to sync. The ", _jsx(_components.code, {
          children: "ALL"
        }), " statement covers existing views, and the ", _jsx(_components.code, {
          children: "FUTURE"
        }), " statement covers views created later."]
      }), "\n", _jsx(_components.p, {
        children: "To grant access to one semantic view instead:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), ", ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "REFERENCES"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " SEMANTIC ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "VIEW"
          }), " YOUR_DATABASE.YOUR_SCHEMA.YOUR_SEMANTIC_VIEW ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " ROLE HIGHTOUCH_ROLE;\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "databricks-grants",
        children: "Databricks grants"
      }), "\n", _jsx(_components.p, {
        children: "Metric views are a Unity Catalog feature, so your Databricks source must have a catalog configured. Hightouch lists metric views only within that catalog."
      }), "\n", _jsxs(_components.p, {
        children: ["Replace ", _jsx(_components.code, {
          children: "YOUR_CATALOG"
        }), ", ", _jsx(_components.code, {
          children: "YOUR_SCHEMA"
        }), ", ", _jsx(_components.code, {
          children: "YOUR_METRIC_VIEW"
        }), ", and ", _jsx(_components.code, {
          children: "HIGHTOUCH_SERVICE_PRINCIPAL"
        }), " with the values configured for your Databricks source. ", _jsx(_components.code, {
          children: "HIGHTOUCH_SERVICE_PRINCIPAL"
        }), " is the service principal's application ID."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-comment",
            children: "-- Allow Hightouch to discover metric views through Databricks metadata"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " USE CATALOG ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " CATALOG ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "system"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " `HIGHTOUCH_SERVICE_PRINCIPAL`;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " USE SCHEMA ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " SCHEMA system.information_schema ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " `HIGHTOUCH_SERVICE_PRINCIPAL`;\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "-- Allow Hightouch to access the selected metric view"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " USE CATALOG ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " CATALOG YOUR_CATALOG ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " `HIGHTOUCH_SERVICE_PRINCIPAL`;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " USE SCHEMA ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " SCHEMA YOUR_CATALOG.YOUR_SCHEMA ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " `HIGHTOUCH_SERVICE_PRINCIPAL`;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "VIEW"
          }), " YOUR_CATALOG.YOUR_SCHEMA.YOUR_METRIC_VIEW ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " `HIGHTOUCH_SERVICE_PRINCIPAL`;\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "The metadata grants don't give access to data outside the catalogs, schemas, and views the service principal can already reach."
      }), "\n", _jsx(_components.p, {
        children: "To grant access across a schema instead of per view:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " SCHEMA YOUR_CATALOG.YOUR_SCHEMA ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " `HIGHTOUCH_SERVICE_PRINCIPAL`;\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "The schema-level grant applies to all current and future tables and views in the schema. Use per-view grants when the service principal should only read selected metric views."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "sync-semantic-or-metric-views",
        children: "Sync semantic or metric views"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Context Hub > Connected data > Integrations"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["In the ", _jsx(_components.strong, {
            children: "External context sources"
          }), " card, select ", _jsx(_components.strong, {
            children: "Add external context"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Snowflake Semantic Views"
          }), " or ", _jsx(_components.strong, {
            children: "Databricks Unity Catalog Metric Views"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Select a connection"
          }), ", select the Snowflake or Databricks source to read from."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Select semantic views"
          }), ", select the views agents should use. Search by the view's full name to find one quickly."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Finish"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-connected-external-context-sources.png",
          alt: "External context sources card with the Add external context button"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The first sync starts as soon as you finish. When it completes, each view appears in ", _jsx(_components.strong, {
          children: "Context Hub > Knowledge > Files"
        }), " with a ", _jsx(_components.strong, {
          children: "Synced"
        }), " badge. Synced files are published by default, so agents can use them right away."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["If no views appear, check the grants for the role or service principal that the selected source uses. If no connections appear, ", _jsx(_components.a, {
            href: "/sources/overview#add-a-source",
            children: "add a Snowflake or Databricks source"
          }), " first."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "how-agents-use-synced-definitions",
        children: "How agents use synced definitions"
      }), "\n", _jsx(_components.p, {
        children: "Each synced file lists the view's terms (its metrics and dimensions) with their formulas and descriptions, followed by the view's full source definition. When a request uses one of those terms, the agent reads the matching file and uses your data team's definition to write its query."
      }), "\n", _jsx(_components.p, {
        children: "Each file also lists the warehouse tables the view references and whether each table is modeled in Hightouch. Agents build queries on the matching Hightouch models. For a table without a model, agents can explain the metric from its definition but don't query the table directly."
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["If you want agents to calculate a synced metric, not only explain it, create ", _jsx(_components.a, {
            href: "/models/creating-models",
            children: "models"
          }), " for the tables its view references."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "synced-definitions-update-every-hour",
        children: "Synced definitions update every hour"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch re-syncs each external context source every hour, so changes your data team makes to a view reach agents without any action from you. To pick up a change sooner, open the source's actions menu in the ", _jsx(_components.strong, {
          children: "External context sources"
        }), " card and select ", _jsx(_components.strong, {
          children: "Sync now"
        }), ". The ", _jsx(_components.strong, {
          children: "Last sync"
        }), " column shows whether the most recent run succeeded."]
      }), "\n", _jsx(_components.p, {
        children: "To change a definition, update the view in your warehouse. The next sync brings the change into Hightouch. You can't edit a synced file's content in Hightouch or delete the file from Knowledge."
      }), "\n", _jsxs(_components.p, {
        children: ["To change which views you sync, select ", _jsx(_components.strong, {
          children: "Edit"
        }), " from the source's actions menu, update your selection, and select ", _jsx(_components.strong, {
          children: "Save changes"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["To stop syncing, select ", _jsx(_components.strong, {
          children: "Delete"
        }), ". Hightouch marks the source's synced files as deleted, and agents stop using them. If you add the views again later, the new source's first sync adds them back as new files."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "sync-files-from-github",
        children: "Sync files from GitHub"
      }), "\n", _jsx(FeatureFlag, {
        featureName: "GitHub external context"
      }), "\n", _jsx(_components.p, {
        children: "Sync files from a GitHub repository when your team maintains metric definitions, glossaries, or playbooks in version control. Agents then work from the same version your team reviews in GitHub, and you don't maintain a second copy in Context Hub."
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch reads the repository with your workspace's GitHub credentials from Git Sync. Before you start, ", _jsx(_components.a, {
          href: "/extensions/git-sync#authenticate-to-git",
          children: "connect GitHub"
        }), " with the Hightouch GitHub app or a GitHub access token, and grant access to the repository you want to sync. Repositories on a self-hosted GitHub Enterprise server and tunneled connections aren't supported."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Context Hub > Connected data > Integrations"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["In the ", _jsx(_components.strong, {
            children: "External context sources"
          }), " card, select ", _jsx(_components.strong, {
            children: "Add external context"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "GitHub"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "Repository"
          }), " and ", _jsx(_components.strong, {
            children: "Branch"
          }), " to read from."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the files and folders agents should use, or select ", _jsx(_components.strong, {
            children: "Sync entire repository"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Finish"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Select a folder when you want new files in it to sync automatically. A selected folder includes every supported file in it, including files your team adds there later. Select individual files when the folder also holds drafts or content agents shouldn't use."
      }), "\n", _jsx(_components.p, {
        children: "You can sync Markdown, plain text, JSON, and Python files. Each file can be up to 5 MB, and each source can include up to 1,000 files and 50 MB in total. You can't select files over 5 MB or more than 1,000 files. If the selected files total more than 50 MB, the sync fails."
      }), "\n", _jsxs(_components.p, {
        children: ["The first sync starts as soon as you finish. Each file appears in ", _jsx(_components.strong, {
          children: "Context Hub > Knowledge > Files"
        }), " with a ", _jsx(_components.strong, {
          children: "Synced"
        }), " badge, named by its path in the repository. Markdown files keep their formatting, and other file types appear as code blocks."]
      }), "\n", _jsxs(_components.p, {
        children: ["GitHub sources follow the same ", _jsx(_components.a, {
          href: "#synced-definitions-update-every-hour",
          children: "hourly schedule and actions"
        }), " as warehouse sources. To change a file's content, commit the change in GitHub. To change the branch or the selected files, select ", _jsx(_components.strong, {
          children: "Edit"
        }), " from the source's actions menu. To sync a different repository, add another GitHub source."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/context-hub/business-context",
            children: "Teach agents your business"
          }), " to add the business rules and instructions that your warehouse views don't cover."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
